import React, { useEffect } from "react";
import { reaction } from "mobx";
import { observer } from "mobx-react";

import {
  WorkflowEventName,
  WorkflowResultDtoWorkflowNameEnum,
} from "../../api-client";
import useStore from "../../helpers/useStore";
import { RegistryContext } from "../../stores/Registry/registry.provider";
import notificationStore from "../../stores/Notification/notificationStore";
import { useWorkflow } from "../../hooks/useWorkflow";

/**
 * Headless, app-level bridge that owns the live subscription to the bulk Rx
 * validation ("Rezeptprüfung") workflow.
 *
 * Mounted once in App.tsx so the subscription stays alive regardless of route /
 * tab unmounts. Streamed batches + the final result are written straight into
 * the RegistryStore, so the Registry Rx tab merely *reads* the current state —
 * no replay or re-subscribe is needed when the user navigates away and back
 * (the listener never detaches, so nothing is ever missed).
 *
 * Modelled on MutationSocketBridge (the scheduler agent's app-level bridge).
 */
const RxValidationSocketBridge: React.FC = observer(() => {
  const registryStore = useStore(RegistryContext);

  const { subscribeToWorkflow, displayProgress } = useWorkflow({
    workflowName: WorkflowResultDtoWorkflowNameEnum.RxValidationBulkWorkflow,
    onWorkflowEvent: (event) => {
      if (event.eventName === WorkflowEventName.RxValidationBulkBatch) {
        const { rows, processed, total } = event.data;
        registryStore.appendValidationRows(rows, processed, total);
      }
    },
    onResult: (result) => {
      registryStore.setValidationFinal(result.data);
      registryStore.setValidationStatus("done");
      // Colour reflects the outcome, not just "the run finished": green only
      // when nothing is wrong, neutral info when prescriptions need attention
      // (mirrors the red/green pill badge — green for clean, red for errors).
      const invalid = result.data.totalInvalid;
      // Rxs that could not be fully checked are called out rather than folded
      // into the finding count — that silence is what made two runs over the
      // same data disagree (MD-184).
      const failed = result.data.totalFailed ?? 0;
      const unchecked = failed > 0 ? ` (${failed} konnten nicht geprüft werden)` : "";
      notificationStore.showMessage(
        invalid > 0
          ? `Rezeptprüfung abgeschlossen — ${invalid} Rezepte mit Fehlern${unchecked}`
          : `Rezeptprüfung abgeschlossen — keine Rezepte mit Fehlern${unchecked}`,
        invalid > 0 || failed > 0 ? "info" : "success",
      );
    },
    onError: () => {
      registryStore.setValidationStatus("error");
    },
    showErrorNotifications: false,
  });

  // Mirror the smoothed progress into the store so the Rx tab's progress bar can
  // read it without owning the subscription.
  useEffect(() => {
    registryStore.setValidationProgress(displayProgress);
  }, [displayProgress, registryStore]);

  // Subscribe whenever a (new) run starts — start() sets validationWorkflowId.
  // The bridge never unmounts, so the subscription persists for the whole
  // session and keeps feeding the store while the user is on other pages.
  useEffect(() => {
    const dispose = reaction(
      () => registryStore.validationWorkflowId,
      (workflowId) => {
        if (workflowId) {
          subscribeToWorkflow({ workflowId, message: "" });
        }
      },
      { fireImmediately: true },
    );
    return dispose;
  }, [registryStore, subscribeToWorkflow]);

  return null;
});

export default RxValidationSocketBridge;
